// API Service Layer that aligns with the backend API documentation
import apiClient from './axios';

class ApiService {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || 'https://n-mgxgoldbackend-434274078606.asia-south1.run.app/api/v1';
  }

  // Set access token
  setAccessToken(token) {
    apiClient.setAuthToken(token);
  }

  // Remove access token
  removeAccessToken() {
    apiClient.clearAuth();
  }

  // Authentication API
  auth = {
    // Get authentication API info
    getInfo: () => apiClient.get('/auth'),
    
    // Send OTP
    sendOTP: (email) => apiClient.post('/auth/send-otp', { email }),
    
    // Send default OTP for admin
    sendDefaultOTPAdmin: (email) => apiClient.post('/auth/send-default-otp-admin', { email }),
    
    // Verify OTP
    verifyOTP: (email, otp) => apiClient.post('/auth/verify-otp', { email, otp }),
    
    // Login with password
    login: (email, password) => apiClient.post('/auth/login', { email, password }),
    
    // Register new user
    register: (userData) => apiClient.post('/auth/register', userData),
    
    // Refresh token
    refreshToken: (refreshToken) => apiClient.post('/auth/refresh-token', { refreshToken }),
    
    // Get user profile
    getProfile: () => apiClient.get('/auth/profile'),
    
    // Update user profile
    updateProfile: (profileData) => apiClient.put('/auth/profile', profileData),
    
    // Change password
    changePassword: (currentPassword, newPassword) => apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword
    }),
    
    // Logout
    logout: () => apiClient.post('/auth/logout')
  };

  // Users API
  users = {
    // Get all users (admin only)
    getAll: (params = {}) => apiClient.get('/users', { params }),
    
    // Get user by ID
    getById: (id) => apiClient.get(`/users/${id}`),
    
    // Create user (admin only)
    create: (userData) => apiClient.post('/users', userData),
    
    // Update user (admin only)
    update: (id, userData) => apiClient.put(`/users/${id}`, userData),
    
    // Delete user (admin only)
    delete: (id) => apiClient.delete(`/users/${id}`)
  };

  // Investments API
  investments = {
    // Get all investments
    getAll: (params = {}) => apiClient.get('/investments', { params }),
    
    // Get investment by ID
    getById: (id) => apiClient.get(`/investments/${id}`),
    
    // Get investments by user ID
    getByUserId: (userId, params = {}) => apiClient.get(`/investments/user/${userId}`, { params }),
    
    // Create investment
    create: (investmentData) => apiClient.post('/investments', investmentData),
    
    // Update investment
    update: (id, investmentData) => apiClient.put(`/investments/${id}`, investmentData),
    
    // Delete investment
    delete: (id) => apiClient.delete(`/investments/${id}`),
    
    // Process ROI payment
    processROI: (id) => apiClient.post(`/investments/${id}/roi`),
    
    // Get ROI history
    getROIHistory: (month, year) => apiClient.get(`/investments/roi-history?month=${month}&year=${year}`),
    
    // Process bulk ROI
    processBulkROI: (data) => apiClient.post('/investments/process-roi', data)
  };

  // Transactions API
  transactions = {
    // Get all transactions
    getAll: (params = {}) => apiClient.get('/transactions', { params }),
    
    // Get transaction by ID
    getById: (id) => apiClient.get(`/transactions/${id}`),
    
    // Get transactions by user ID
    getByUserId: (userId, params = {}) => apiClient.get(`/transactions/user/${userId}`, { params }),
    
    // Create transaction
    create: (transactionData) => apiClient.post('/transactions', transactionData),
    
    // Update transaction
    update: (id, transactionData) => apiClient.put(`/transactions/${id}`, transactionData),
    
    // Approve transaction (admin only)
    approve: (id) => apiClient.post(`/transactions/${id}/approve`),
    
    // Reject transaction (admin only)
    reject: (id, reason) => apiClient.post(`/transactions/${id}/reject`, { reason })
  };

  // Bank Details API
  bankDetails = {
    // Get all bank details
    getAll: (params = {}) => apiClient.get('/bank-details', { params }),
    
    // Get bank detail by ID
    getById: (id) => apiClient.get(`/bank-details/${id}`),
    
    // Get bank details by user ID
    getByUserId: (userId, params = {}) => apiClient.get(`/bank-details/user/${userId}`, { params }),
    
    // Create bank detail
    create: (bankData) => apiClient.post('/bank-details', bankData),
    
    // Update bank detail
    update: (id, bankData) => apiClient.put(`/bank-details/${id}`, bankData),
    
    // Delete bank detail
    delete: (id) => apiClient.delete(`/bank-details/${id}`),
    
    // Set primary bank account
    setPrimary: (id) => apiClient.post(`/bank-details/${id}/primary`)
  };

  // KYC API
  kyc = {
    // Get all KYC records
    getAll: (params = {}) => apiClient.get('/kyc', { params }),
    
    // Get KYC record by ID
    getById: (id) => apiClient.get(`/kyc/${id}`),
    
    // Get KYC record by user ID
    getByUserId: (userId, params = {}) => apiClient.get(`/kyc/user/${userId}`, { params }),
    
    // Update KYC record
    update: (kycData) => apiClient.post('/kyc', kycData),
    
    // Approve KYC (admin only)
    approve: (id) => apiClient.post(`/kyc/${id}/approve`),
    
    // Reject KYC (admin only)
    reject: (id, rejectionReason) => apiClient.post(`/kyc/${id}/reject`, { rejectionReason })
  };

  // Notifications API
  notifications = {
    // Get all notifications
    getAll: (params = {}) => apiClient.get('/notifications', { params }),
    
    // Get notification by ID
    getById: (id) => apiClient.get(`/notifications/${id}`),
    
    // Create notification
    create: (notificationData) => apiClient.post('/notifications', notificationData),
    
    // Mark notification as read
    markAsRead: (id) => apiClient.post(`/notifications/${id}/read`),
    
    // Mark notification as unread
    markAsUnread: (id) => apiClient.post(`/notifications/${id}/unread`),
    
    // Delete notification
    delete: (id) => apiClient.delete(`/notifications/${id}`)
  };

  // Plans API
  plans = {
    // Get all investment plans
    getAll: (params = {}) => apiClient.get('/plans', { params }),
    
    // Get investment plan by ID
    getById: (id) => apiClient.get(`/plans/${id}`),
    
    // Create investment plan (admin only)
    create: (planData) => apiClient.post('/plans', planData),
    
    // Update investment plan (admin only)
    update: (id, planData) => apiClient.put(`/plans/${id}`, planData),
    
    // Delete investment plan (admin only)
    delete: (id) => apiClient.delete(`/plans/${id}`)
  };

  // Referrals API
  referrals = {
    // Get referral data
    getData: () => apiClient.get('/referrals'),
    
    // Get referral stats
    getStats: () => apiClient.get('/referrals/stats'),
    
    // Generate referral code
    generateCode: () => apiClient.post('/referrals/generate')
  };

  // Dashboard API
  dashboard = {
    // Get admin dashboard stats (admin only)
    getAdminStats: () => apiClient.get('/dashboard/admin'),
    
    // Get user dashboard stats
    getUserStats: (userId) => apiClient.get(`/dashboard/user/${userId}`)
  };

  // Gold Rates API
  goldRates = {
    // Get current gold rate
    getCurrent: () => apiClient.get('/gold-rates'),
    
    // Get gold rate history
    getHistory: (days = 30) => apiClient.get(`/gold-rates/history?days=${days}`),
    
    // Update gold rate (admin only)
    update: (rateData) => apiClient.post('/gold-rates', rateData)
  };

  // Settings/Configuration API (admin only)
  settings = {
    // Get all key-value settings
    getAll: () => apiClient.get('/settings/all'),
    
    // Get specific setting by key
    get: (key) => apiClient.get(`/settings/${key}`),
    
    // Update or create setting
    update: (key, value) => apiClient.put(`/settings/${key}`, { value }),
    
    // Delete setting
    delete: (key) => apiClient.delete(`/settings/${key}`),
    
    // Branding settings (separate endpoints)
    getBranding: () => apiClient.get('/settings/branding'),
    updateBranding: (formData) => apiClient.put('/settings/branding', formData)
  };

  // Health Checks API
  health = {
    // Server health
    server: () => apiClient.get('/health'),
    
    // Redis health
    redis: () => apiClient.get('/health/redis'),
    
    // Database health
    database: () => apiClient.get('/health/db')
  };
}

export default new ApiService();
